import { Form, Formik } from "formik";
import {
  CreateGeneratorDto,
  PatchGeneratorDto,
  Generator,
} from "@/models/Generator.ts";
import * as yup from "yup";
import { FormikInput } from "@/components/common/form/FormikInput.tsx";
import { Button } from "@/components/base/Button.tsx";
import { FormikDateInput } from "@/components/common/form/FormikDateInput.tsx";

type CreateEditGeneratorFormProps = {
  onSubmit: (values: CreateGeneratorDto | PatchGeneratorDto) => Promise<void>;
  initialGeneratorData?: Generator;
  onCancel?: () => void;
  tenantId: string;
  isEditable?: boolean;
};

const validationSchema = yup.object({
  make: yup.string().required("Make is required"),
  modelNumber: yup.string().required("Model number is required"),
});

export const CreateEditGeneratorForm = ({
  initialGeneratorData,
  onCancel,
  onSubmit,
  tenantId,
  isEditable,
}: CreateEditGeneratorFormProps) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };
  return (
    <Formik
      initialValues={{
        airFilter: initialGeneratorData?.airFilter ?? "",
        battery: initialGeneratorData?.battery ?? "",
        batteryChangeDate: initialGeneratorData?.batteryChangeDate ?? null,
        oilFilter: initialGeneratorData?.oilFilter ?? "",
        installedDate: initialGeneratorData?.installedDate ?? null,
        customerId: initialGeneratorData?.customerId ?? "",
        notes: initialGeneratorData?.notes ?? "",
        kwRating: initialGeneratorData?.kwRating ?? "",
        maintenanceKit: initialGeneratorData?.maintenanceKit ?? "",
        make: initialGeneratorData?.make ?? "",
        modelNumber: initialGeneratorData?.modelNumber ?? "",
        serialNumber: initialGeneratorData?.serialNumber ?? "",
        mobileLinkExp: initialGeneratorData?.mobileLinkExp ?? "",
        lastServiceDate: initialGeneratorData?.lastServiceDate ?? null,
        nextServiceDate: initialGeneratorData?.nextServiceDate ?? null,
        planType: initialGeneratorData?.planType ?? "",
        renewStartDate: initialGeneratorData?.renewStartDate ?? null,
        renewEndDate: initialGeneratorData?.renewEndDate ?? null,
        serviceAgreementPrice: initialGeneratorData?.serviceAgreementPrice ?? 0,
        warranty: initialGeneratorData?.warranty ?? "",
        warrantyExpirationDate:
          initialGeneratorData?.warrantyExpirationDate ?? null,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        let submitData: CreateGeneratorDto | PatchGeneratorDto = {
          tenantId: tenantId,
          battery: values.battery,
          batteryChangeDate: values.batteryChangeDate,
          oilFilter: values.oilFilter,
          installedDate: values.installedDate,
          customerId: values.customerId,
          notes: values.notes,
          kwRating: values.kwRating,
          maintenanceKit: values.maintenanceKit,
          make: values.make,
          modelNumber: values.modelNumber,
          serialNumber: values.serialNumber,
          mobileLinkExp: values.mobileLinkExp,
          lastServiceDate: values.lastServiceDate,
          nextServiceDate: values.nextServiceDate,
          planType: values.planType,
          renewStartDate: values.renewStartDate,
          renewEndDate: values.renewEndDate,
          serviceAgreementPrice: values.serviceAgreementPrice,
          warranty: values.warranty ?? "",
          warrantyExpirationDate: values.warrantyExpirationDate ?? "",
        } as CreateGeneratorDto;
        if (initialGeneratorData?.id) {
          submitData = {
            id: initialGeneratorData.id,
            ...submitData,
          } as PatchGeneratorDto;
        }

        onSubmit(submitData).finally(() => setSubmitting(false));
        return;
      }}
    >
      {({ handleSubmit }) => (
        <Form
          className="flex flex-col sm:grid sm:grid-cols-3 gap-4 m-auto w-64 sm:w-auto max-w-screen-md"
          onSubmit={handleSubmit}
        >
          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Generator Information</h3>
            <FormikInput
              name="make"
              type="text"
              id="make"
              label="Make"
              disabled={!isEditable}
            />
            <FormikInput
              name="modelNumber"
              type="text"
              id="modelNumber"
              label="Model Number"
              disabled={!isEditable}
            />
            <FormikInput
              name="serialNumber"
              type="text"
              id="serialNumber"
              label="Serial Number"
              disabled={!isEditable}
            />
            <FormikInput
              name="kwRating"
              type="number"
              id="kwRating"
              label="KW Rating"
              disabled={!isEditable}
            />
            <FormikDateInput
              name="installedDate"
              type="date"
              id="installedDate"
              label="Installed Date"
              disabled={!isEditable}
            />
            <FormikInput
              name="notes"
              type="text"
              id="notes"
              label="Notes"
              disabled={!isEditable}
            />
          </div>

          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Maintenance Information</h3>

            <FormikInput
              name="maintenanceKit"
              type="text"
              id="maintenanceKit"
              label="Maintenance Kit"
              disabled={!isEditable}
            />

            <FormikInput
              name="airFilter"
              type="text"
              id="airFilter"
              label="Air Filter"
              disabled={!isEditable}
            />
            <FormikInput
              name="battery"
              type="text"
              id="battery"
              label="Battery"
              disabled={!isEditable}
            />
            <FormikDateInput
              name="batteryChangeDate"
              type="date"
              id="batteryChangeDate"
              label="Battery Change Date"
              disabled={!isEditable}
            />
            <FormikInput
              name="oilFilter"
              type="text"
              id="oilFilter"
              label="Oil Filter"
              disabled={!isEditable}
            />

            <FormikDateInput
              name="lastServiceDate"
              type="date"
              id="lastServiceDate"
              label="Last Service Date"
              disabled={!isEditable}
            />
            <FormikDateInput
              name="nextServiceDate"
              type="date"
              id="nextServiceDate"
              label="Next Service Date"
              disabled={!isEditable}
            />
          </div>

          <div className="flex md:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Agreement Information</h3>

            <FormikInput
              name="planType"
              type="text"
              id="planType"
              label="Plan Type"
              disabled={!isEditable}
            />
            <FormikInput
              name="serviceAgreementPrice"
              type="number"
              id="serviceAgreementPrice"
              label="Service Agreement Price ($)"
              disabled={!isEditable}
            />
            <FormikDateInput
              name="renewStartDate"
              type="date"
              id="renewStartDate"
              label="Renew Start Date"
              disabled={!isEditable}
            />
            <FormikDateInput
              name="renewEndDate"
              type="date"
              id="renewEndDate"
              label="Renew End Date"
              disabled={!isEditable}
            />
            <FormikInput
              name="warranty"
              type="text"
              id="warranty"
              label="Warranty"
              disabled={!isEditable}
            />
            <FormikDateInput
              name="warrantyExpirationDate"
              type="date"
              id="warrantyExpirationDate"
              label="Warranty Expiration Date"
              disabled={!isEditable}
            />
          </div>
          {isEditable && (
            <div className="flex md:col-span-3 flex-row gap-4 justify-end">
              <Button
                variant={"secondary"}
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
